import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UploadCloud, X, Star, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "../../lib/api";

const CONDITIONS = ["New / Unused", "Excellent", "Very Good", "Good", "Fair"];
const empty = {
  brand: "", model: "", title: "", category: "Handbags", subcategory: "", description: "",
  price: "", currency: "AUD", original_retail_price: "", condition: "Excellent", condition_notes: "",
  color: "", material: "", hardware: "", dimensions: "", year: "", serial_info: "", date_code: "",
  authentication_status: "Authenticated in-house", authentication_provider: "", authentication_method: "",
  authentication_date: "", authentication_reference: "", certificate_available: false, certificate_type: "",
  authentication_public: true, included_accessories: "", images: [], featured: false, new_arrival: true,
  inventory_quantity: 1, status: "available",
};

function Field({ label, children, className = "" }) {
  return <div className={className}><label className="eyebrow block mb-2">{label}</label>{children}</div>;
}

// Downscale + compress an image in the browser before upload. Keeps requests
// small enough for the Cloudflare/ingress body limit and converts iPhone HEIC
// to JPEG. Falls back to the original file if anything goes wrong.
async function compressImage(file, maxDim = 2200, quality = 0.85) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.size < 350 * 1024) return file; // already small
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUrl;
    });
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) return file;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    w = Math.round(w * scale);
    h = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    const out = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
    return out.size < file.size ? out : (file.type === "image/jpeg" ? file : out);
  } catch {
    return file;
  }
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const inp = "w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    if (id) {
      api.get(`/products/${id}`).then((r) => {
        const d = r.data;
        setForm({ ...empty, ...d, included_accessories: (d.included_accessories || []).join(", "),
          original_retail_price: d.original_retail_price ?? "" });
      }).catch(() => toast.error("Could not load product"));
    }
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadCount(files.length);
    try {
      // Compress every image in parallel (downscale + JPEG), then upload in parallel.
      const prepared = await Promise.all(files.map((raw) => compressImage(raw).then((f) => ({ raw, f }))));
      const okFiles = [];
      for (const { raw, f } of prepared) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${raw.name || "An image"} is too large even after compression. Please use a smaller photo.`);
        } else {
          okFiles.push(f);
        }
      }
      const results = await Promise.allSettled(okFiles.map((f) => {
        const fd = new FormData(); fd.append("file", f);
        return api.post("/admin/upload", fd).then((r) => r.data.url);
      }));
      const urls = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failed = results.length - urls.length;
      if (urls.length) {
        setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
        toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
      }
      if (failed) toast.error(`${failed} image${failed > 1 ? "s" : ""} failed to upload. Please try again.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      setUploadCount(0);
    }
  };

  const move = (i, dir) => {
    const arr = [...form.images]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setForm({ ...form, images: arr });
  };
  const makePrimary = (i) => { const arr = [...form.images]; const [x] = arr.splice(i, 1); setForm({ ...form, images: [x, ...arr] }); };
  const removeImg = (i) => setForm({ ...form, images: form.images.filter((_, k) => k !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.title || !form.price) { toast.error("Brand, title and price are required"); return; }
    setSaving(true);
    const payload = {
      ...form, price: parseFloat(form.price),
      original_retail_price: form.original_retail_price ? parseFloat(form.original_retail_price) : null,
      inventory_quantity: parseInt(form.inventory_quantity) || 1,
      included_accessories: form.included_accessories.split(",").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.availability_status; delete payload.available; delete payload.id; delete payload.slug;
    delete payload.created_at; delete payload.updated_at; delete payload.sold_at;
    try {
      if (id) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(id ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-4xl">
      <h1 className="font-serif text-3xl text-pitch mb-8">{id ? "Edit Product" : "Add Product"}</h1>

      {/* Images */}
      <div className="bg-white border border-linen p-6 mb-6">
        <h2 className="eyebrow mb-4">Images</h2>
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files)); }}
          onClick={() => fileRef.current?.click()} data-testid="admin-image-dropzone"
          className="border-2 border-dashed border-linen hover:border-pitch cursor-pointer py-10 text-center transition-colors">
          <UploadCloud className="w-8 h-8 mx-auto text-stonemuted mb-2" strokeWidth={1.2} />
          <p className="text-sm text-charcoal/70">{uploading ? `Compressing & uploading ${uploadCount} image${uploadCount > 1 ? "s" : ""}…` : "Drag & drop images here, or click to browse — add several at once"}</p>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="admin-image-input"
            onChange={(e) => upload(Array.from(e.target.files))} />
        </div>
        {form.images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
            {form.images.map((img, i) => (
              <div key={i} className="relative group aspect-[4/5] bg-bone overflow-hidden border border-linen">
                <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute top-1 left-1 bg-pitch text-ivory text-[9px] font-mono px-1.5 py-0.5">PRIMARY</span>}
                <div className="absolute inset-0 bg-pitch/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button type="button" onClick={() => makePrimary(i)} title="Set primary" className="p-1.5 text-ivory hover:text-champagne"><Star className="w-4 h-4" /></button>
                  <button type="button" onClick={() => move(i, -1)} className="p-1.5 text-ivory"><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="p-1.5 text-ivory"><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => removeImg(i)} className="p-1.5 text-ivory hover:text-vermilion"><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Core */}
      <div className="bg-white border border-linen p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Brand *">
          <input list="brand-list" required value={form.brand} onChange={set("brand")} className={inp} data-testid="pf-brand" />
          <datalist id="brand-list">{brands.map((b) => <option key={b.id} value={b.name} />)}</datalist>
        </Field>
        <Field label="Model"><input value={form.model} onChange={set("model")} className={inp} /></Field>
        <Field label="Title *" className="sm:col-span-2"><input required value={form.title} onChange={set("title")} className={inp} data-testid="pf-title" /></Field>
        <Field label="Category">
          <select value={form.category} onChange={set("category")} className={inp} data-testid="pf-category">
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Subcategory"><input value={form.subcategory} onChange={set("subcategory")} className={inp} /></Field>
        <Field label="Description" className="sm:col-span-2"><textarea rows={3} value={form.description} onChange={set("description")} className={inp} /></Field>
        <Field label="Price (AUD) *"><input required type="number" step="0.01" value={form.price} onChange={set("price")} className={inp} data-testid="pf-price" /></Field>
        <Field label="Original Retail (optional)"><input type="number" step="0.01" value={form.original_retail_price} onChange={set("original_retail_price")} className={inp} /></Field>
        <Field label="Condition"><select value={form.condition} onChange={set("condition")} className={inp} data-testid="pf-condition">{CONDITIONS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Inventory Quantity"><input type="number" value={form.inventory_quantity} onChange={set("inventory_quantity")} className={inp} data-testid="pf-inventory" /></Field>
        <Field label="Condition Notes" className="sm:col-span-2"><textarea rows={2} value={form.condition_notes} onChange={set("condition_notes")} className={inp} placeholder="Disclose specific imperfections…" /></Field>
      </div>

      {/* Attributes */}
      <div className="bg-white border border-linen p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Colour"><input value={form.color} onChange={set("color")} className={inp} /></Field>
        <Field label="Material"><input value={form.material} onChange={set("material")} className={inp} /></Field>
        <Field label="Hardware"><input value={form.hardware} onChange={set("hardware")} className={inp} /></Field>
        <Field label="Dimensions"><input value={form.dimensions} onChange={set("dimensions")} className={inp} /></Field>
        <Field label="Year"><input value={form.year} onChange={set("year")} className={inp} /></Field>
        <Field label="Included Accessories (comma separated)"><input value={form.included_accessories} onChange={set("included_accessories")} className={inp} /></Field>
      </div>

      {/* Authentication */}
      <div className="bg-white border border-linen p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <h2 className="eyebrow sm:col-span-2">Authentication <span className="text-vermilion normal-case">— only enter information you have verified</span></h2>
        <Field label="Authentication Status"><input value={form.authentication_status} onChange={set("authentication_status")} className={inp} /></Field>
        <Field label="Provider"><input value={form.authentication_provider} onChange={set("authentication_provider")} className={inp} /></Field>
        <Field label="Method"><input value={form.authentication_method} onChange={set("authentication_method")} className={inp} /></Field>
        <Field label="Reference"><input value={form.authentication_reference} onChange={set("authentication_reference")} className={inp} /></Field>
        <Field label="Serial Info"><input value={form.serial_info} onChange={set("serial_info")} className={inp} /></Field>
        <Field label="Date Code"><input value={form.date_code} onChange={set("date_code")} className={inp} /></Field>
        <Field label="Certificate Type"><input value={form.certificate_type} onChange={set("certificate_type")} className={inp} /></Field>
        <div className="flex flex-col justify-end gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.certificate_available} onChange={set("certificate_available")} /> Certificate available</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.authentication_public} onChange={set("authentication_public")} data-testid="pf-auth-public" /> Show authentication publicly</label>
        </div>
      </div>

      {/* Flags */}
      <div className="bg-white border border-linen p-6 mb-8 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="pf-featured" /> Featured</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.new_arrival} onChange={set("new_arrival")} data-testid="pf-new-arrival" /> New Arrival</label>
        <label className="flex items-center gap-2 text-sm">Status:
          <select value={form.status} onChange={set("status")} className="border border-linen px-2 py-1"><option value="available">Available</option><option value="archived">Archived</option><option value="sold">Sold</option></select>
        </label>
      </div>

      <div className="flex gap-4">
        <button type="submit" disabled={saving} data-testid="pf-submit"
          className="bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-charcoal transition-colors disabled:opacity-50">
          {saving ? "Saving…" : id ? "Update Product" : "Create Product"}
        </button>
        <button type="button" onClick={() => navigate("/admin/products")} className="border border-pitch text-xs font-mono uppercase tracking-[0.2em] px-8 py-4">Cancel</button>
      </div>
    </form>
  );
}
